import { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button, Icon } from '../ds';
import { font } from '../tokens';
import './access-explainer.css';

const CONCEPTS = [
  {
    key: 'catalog', label: 'Catalog visibility', title: 'Find an app in the catalog',
    lanes: [
      { name: 'Alice', role: 'Any Abridge user', steps: ['No Access to Vibes yet', 'Public'], outcome: 'Find app & URLs*', allowed: true },
      { name: 'Bob', role: 'Owner or collaborator', steps: ['Access to Vibes', 'Private / Public'], outcome: 'Find in portal', allowed: true },
      { name: 'Charlie', role: 'Other teammate', steps: ['Not a collaborator or owner', 'Private'], outcome: 'Not shown', allowed: false },
    ],
  },
  {
    key: 'collaborators', label: 'Collaborators', title: 'Develop and manage an app',
    lanes: [
      { name: 'Alice', role: 'Owner or collaborator', steps: ['GitHub account + Vibes access', 'Collaborator permissions'], outcome: 'Develop & manage', allowed: true },
      { name: 'Bob', role: 'Public app user', steps: ['Can use the app', 'Not a collaborator'], outcome: 'No editing access', allowed: false },
    ],
  },
  {
    key: 'app', label: 'App access', title: 'Use a web app',
    lanes: [
      { name: 'Alice', role: 'Any Abridge user', steps: ['Public app URL', 'Sign in with Abridge Google'], outcome: 'Use the app', allowed: true },
      { name: 'Bob', role: 'Allowed private app user', steps: ['Private app URL', 'Included in App access'], outcome: 'Use the app', allowed: true },
      { name: 'Charlie', role: 'Other teammate', steps: ['Private app URL', 'Not included in App access'], outcome: 'Access denied', allowed: false },
    ],
  },
  {
    key: 'mcp', label: 'MCP access', title: 'Discover apps or work on them through the MCP',
    lanes: [
      { name: 'Alice', role: 'Public app viewer', steps: ['List Public apps', 'Read catalog summaries'], outcome: 'Discover apps', allowed: true },
      { name: 'Bob', role: 'Public app viewer', steps: ['No collaborator access', 'Inspect or operate on app'], outcome: 'MCP action denied', allowed: false },
      { name: 'Charlie', role: 'Owner or collaborator', steps: ['Current collaborator access', 'Resolve app & use tools'], outcome: 'Work on the app', allowed: true },
    ],
  },
] as const;

export function AccessExplainer() {
  const [open, setOpen] = useState(false);
  return <>
    <button type="button" className="access-explainer-link" aria-haspopup="dialog" onClick={() => setOpen(true)}>
      How access works
    </button>
    {open && createPortal(<AccessExplainerDialog onClose={() => setOpen(false)} />, document.body)}
  </>;
}

function AccessExplainerDialog({ onClose }: { onClose: () => void }) {
  const id = useId();
  const dialog = useRef<HTMLDialogElement>(null);
  const [selected, setSelected] = useState<(typeof CONCEPTS)[number]['key']>('catalog');
  const active = CONCEPTS.find(concept => concept.key === selected)!;
  useEffect(() => {
    const element = dialog.current;
    const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    element?.showModal();
    return () => {
      element?.close();
      previousFocus?.focus();
    };
  }, []);

  return <dialog ref={dialog} className="access-explainer" style={{ fontFamily: font }} aria-labelledby={`${id}-title`}
    onCancel={event => { event.preventDefault(); onClose(); }}
    onClick={event => {
      if (event.target !== event.currentTarget) return;
      const bounds = event.currentTarget.getBoundingClientRect();
      if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) onClose();
    }}>
    <header className="access-explainer-header">
      <h2 id={`${id}-title`}>How access works</h2>
      <Button variant="outlined" size="sm" onPress={onClose}>Close</Button>
    </header>
    <div className="environment-selector access-explainer-pills" role="group" aria-label="Access concepts">
      {CONCEPTS.map(concept => <button key={concept.key} type="button" aria-pressed={selected === concept.key}
        onPointerEnter={() => setSelected(concept.key)} onFocus={() => setSelected(concept.key)} onClick={() => setSelected(concept.key)}>
        {concept.label}
      </button>)}
    </div>
    <span className="access-explainer-announcement" role="status" aria-live="polite">{active.title}</span>
    <div className="access-workflows">
      {CONCEPTS.map(concept => <section key={concept.key} className="access-workflow" data-active={selected === concept.key}
        aria-hidden={selected !== concept.key} aria-label={`${concept.label} workflow`}>
        <div className="access-workflow-heading"><h3>{concept.title}</h3><span>Example people</span></div>
        <div className="access-workflow-lanes">
          {concept.lanes.map(lane => <div key={lane.name} className="access-workflow-lane">
            <div className="access-workflow-person">
              <div className="access-workflow-figure" aria-hidden="true">
                <span className="access-workflow-face">{!lane.allowed ? '😢' : lane.name === 'Alice' ? '🙂' : '😊'}</span>
              </div>
              <strong>{lane.name}</strong><span>{lane.role}</span>
            </div>
            <span className="access-workflow-arrow"><Icon name="arrow-right" size="sm" /></span>
            <div className="access-workflow-step">{lane.steps[0]}</div>
            <span className="access-workflow-arrow"><Icon name="arrow-right" size="sm" /></span>
            <div className="access-workflow-step">{lane.steps[1]}</div>
            <span className="access-workflow-arrow"><Icon name="arrow-right" size="sm" /></span>
            <div className="access-workflow-outcome" data-allowed={lane.allowed}>
              <Icon name={lane.allowed ? 'check-circle' : 'close-circle-filled'} size="sm" /><span>{lane.outcome}</span>
            </div>
          </div>)}
        </div>
        {concept.key === 'catalog' && <p className="access-explainer-footnote">* Accessible through the public catalog.</p>}
      </section>)}
    </div>
  </dialog>;
}
